import { getInstanceDefinition } from '../cardLookup';
import {
  grantGold, grantXp, grantPhysicalAttack, grantMagicAttack, grantLight, grantBuys,
  drawCards, destroyFromHand, discardFromHand, moveHallCard,
} from '../effectHelpers';
import { hasDarknessAtRank } from '../../combat/lightDarkness';

function updatePlayer(state, playerId, updateFn) {
  return { ...state, players: state.players.map((p) => (p.id === playerId ? updateFn(p) : p)) };
}

// Familiar abilities are indexed 0/1/2 directly against
// cardDefinitions[i].familiarAbilities (xpCost 0/3/6, in that fixed order) -
// deliberately NOT reusing the effectRegistry's timing-bucket Map, since a
// familiar's natural identifier (its position in that array) doesn't line up
// with "index within the village or dungeon bucket" the way every other
// card's abilityText does. See actions/familiarActions.js for the caller.
const familiarRegistry = new Map();

function registerFamiliar(definitionId, resolvers) {
  familiarRegistry.set(definitionId, resolvers);
}

export function getFamiliarHandler(definitionId, abilityIndex) {
  const resolvers = familiarRegistry.get(definitionId);
  return resolvers ? resolvers[abilityIndex] : undefined;
}

function cancelOneBattleEffect(state, { playerId, choices }) {
  const targetRank = choices?.hallRankIndex ?? state.dungeonHall.findIndex(Boolean);
  if (targetRank === -1 || !state.dungeonHall[targetRank]) throw new Error('No monster present to target.');
  const monsterInstanceId = state.dungeonHall[targetRank];
  return updatePlayer(state, playerId, (p) => ({
    ...p, turnFlags: { ...p.turnFlags, cancelledBattleInstanceIds: [...p.turnFlags.cancelledBattleInstanceIds, monsterInstanceId] },
  }));
}

// --- Battle Hawk ------------------------------------------------------------

registerFamiliar('familiar-battle-hawk', [
  (state, { playerId }) => grantGold(state, playerId, 2),
  (state, { playerId }) => grantLight(state, playerId, 1),
  (state, { playerId }) => grantPhysicalAttack(drawCards(state, playerId, 1), playerId, 1),
]);

// --- Brownie ------------------------------------------------------------

function brownieDestroyCandidates(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand; // any card in hand (the familiar itself is never in player.hand)
}

registerFamiliar('familiar-brownie', [
  (state, { playerId }) => grantBuys(grantGold(state, playerId, 1), playerId, 1),
  (state, { playerId }) => grantMagicAttack(state, playerId, 1),
  (state, { playerId, choices }) => {
    const targetId = choices?.instanceId ?? brownieDestroyCandidates(state, { playerId })[0];
    if (!targetId) throw new Error('No card in hand to destroy.');
    return drawCards(destroyFromHand(state, playerId, targetId), playerId, 1);
  },
]);

// --- Dire Wolf ------------------------------------------------------------

registerFamiliar('familiar-dire-wolf', [
  (state, { playerId }) => drawCards(state, playerId, 1),
  (state, { playerId }) => grantPhysicalAttack(state, playerId, 1),
  (state, { choices }) => {
    const a = choices?.rankA ?? 0;
    const b = choices?.rankB ?? 1;
    return moveHallCard(state, a, b);
  },
]);

// --- Imp ------------------------------------------------------------

function impDiscardCandidates(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand;
}

registerFamiliar('familiar-imp', [
  (state, { playerId }) => grantXp(state, playerId, 1),
  (state, { choices }) => {
    const a = choices?.rankA ?? 0;
    const b = choices?.rankB ?? 1;
    if (Math.abs(a - b) !== 1) throw new Error('Must switch two adjacent ranks.');
    return moveHallCard(state, a, b);
  },
  (state, { playerId, choices }) => {
    const toDiscard = (choices?.discardInstanceIds && choices.discardInstanceIds.length === 2)
      ? choices.discardInstanceIds
      : impDiscardCandidates(state, { playerId }).slice(0, 2);
    if (toDiscard.length < 2) throw new Error('Need 2 cards in hand to discard.');
    let nextState = state;
    for (const id of toDiscard) nextState = discardFromHand(nextState, playerId, id);
    return drawCards(nextState, playerId, 2);
  },
]);

// --- Lesser Phoenix ---------------------------------------------------------
// Every other familiar stays in play, usable turn after turn, until its
// abilities are used - only THEN is it discarded, and only at end of turn
// (rulebook, "Familiars"; see turnActions.endTurn). Lesser Phoenix is the
// one exception: all 3 of its abilities print "Discard this card to ..." -
// the discard is part of the ability's own cost, not the general end-of-turn
// rule, so it leaves play (and can't be used again) the instant any one of
// them resolves. Matt, confirming this directly: "The phoenix actually part
// of its ability is discard this card in order to do the effect." Each
// handler runs its own effect first, then discards - endTurn's own
// familiarUsed check already re-tests inPlayInstanceIds.includes(...), so it
// naturally no-ops for an already-discarded Phoenix instead of double-
// discarding it.
function discardPhoenixNow(state, playerId, instanceId) {
  return updatePlayer(state, playerId, (p) => ({
    ...p,
    inPlayInstanceIds: p.inPlayInstanceIds.filter((id) => id !== instanceId),
    discard: [...p.discard, instanceId],
  }));
}

registerFamiliar('familiar-lesser-phoenix', [
  (state, { playerId, instanceId }) => discardPhoenixNow(grantGold(state, playerId, 3), playerId, instanceId),
  (state, ctx) => discardPhoenixNow(cancelOneBattleEffect(state, ctx), ctx.playerId, ctx.instanceId),
  (state, { playerId, instanceId }) => discardPhoenixNow(drawCards(state, playerId, 2), playerId, instanceId),
]);

// --- Pseudodragon ------------------------------------------------------------

registerFamiliar('familiar-pseudodragon', [
  (state, { playerId }) => grantBuys(state, playerId, 1), // "you may buy 2 cards" = 1 base + 1 bonus
  (state, { playerId }) => grantLight(state, playerId, 2),
  (state, { playerId }) => grantMagicAttack(state, playerId, 2),
]);

// --- Shadow Cat ------------------------------------------------------------

function shadowCatVillagerCandidates(state, { playerId }) {
  const player = state.players.find((p) => p.id === playerId);
  return player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Villager'));
}

registerFamiliar('familiar-shadow-cat', [
  (state, { playerId, choices }) => {
    const targetId = choices?.instanceId ?? shadowCatVillagerCandidates(state, { playerId })[0];
    if (!targetId) throw new Error('No villager present to destroy.');
    return destroyFromHand(state, playerId, targetId);
  },
  // "If there is Darkness, Physical Attack +2." Dungeon-timing (resolves
  // before a monster is chosen), same constraint as hero-caliginite-l3 -
  // see hasDarknessAtRank's doc comment. Without a declared rank, falls back
  // to the same conservative "any Light present -> can't guarantee it" proxy.
  (state, { playerId, choices }) => {
    if (choices?.hallRankIndex !== undefined) {
      if (!hasDarknessAtRank(state, playerId, choices.hallRankIndex)) {
        throw new Error("Shadow Cat's bonus only applies if there is Darkness at the chosen rank.");
      }
      return grantPhysicalAttack(state, playerId, 2);
    }
    const player = state.players.find((p) => p.id === playerId);
    const hasLight = player.hand.some((id) => (getInstanceDefinition(state, id).light || 0) > 0);
    if (hasLight) throw new Error("Shadow Cat's bonus isn't guaranteed with Light present - declare choices.hallRankIndex to check a specific rank.");
    return grantPhysicalAttack(state, playerId, 2);
  },
  cancelOneBattleEffect,
]);
